import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

export interface PodiumEntry {
  teamId: number;
  rank: 1 | 2 | 3;
  teamName: string;
  startNumber: number;
  primaryValue: string;
  primaryLabel?: string;
  /** Doplnkovy secondary text (napr. "NP", "+5 s trest"). */
  secondaryValue?: string;
}

interface PodiumProps {
  entries: PodiumEntry[];
  /** Zobrazit ?startovni cislo pod jmenem tymu. */
  showStartNumber?: boolean;
}

const PODIUM_GRADIENTS: Record<1 | 2 | 3, string> = {
  1: 'linear-gradient(160deg, #FFD54F 0%, #FFB300 55%, #FF8F00 100%)',
  2: 'linear-gradient(160deg, #ECEFF1 0%, #B0BEC5 55%, #78909C 100%)',
  3: 'linear-gradient(160deg, #D4A373 0%, #A0703A 55%, #6D4C2A 100%)',
};

const PODIUM_TEXT_COLOR: Record<1 | 2 | 3, string> = {
  1: '#2d1f00',
  2: '#1a1a1a',
  3: '#FFFFFF',
};

const PODIUM_HEIGHT: Record<1 | 2 | 3, number> = {
  1: 220,
  2: 184,
  3: 164,
};

const PODIUM_LABEL: Record<1 | 2 | 3, string> = {
  1: '1. místo',
  2: '2. místo',
  3: '3. místo',
};

/**
 * Podium pro top 3 (discipline nebo overall).
 * Desktop: 3 karty vedle sebe v poradi 2-1-3 (1. misto centralne, vyssi).
 * Mobil: sloupec 1-2-3.
 */
export const Podium = ({ entries, showStartNumber = true }: PodiumProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const byRank: Record<1 | 2 | 3, PodiumEntry | undefined> = {
    1: entries.find((e) => e.rank === 1),
    2: entries.find((e) => e.rank === 2),
    3: entries.find((e) => e.rank === 3),
  };

  // Desktop poradi: 2 | 1 | 3 (1. misto visually dominant uprostred)
  // Mobil poradi: 1 | 2 | 3 (shora)
  const order: (1 | 2 | 3)[] = isMobile ? [1, 2, 3] : [2, 1, 3];

  const visibleEntries = order.map((rank) => byRank[rank]).filter((e): e is PodiumEntry => !!e);
  if (visibleEntries.length === 0) return null;

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={isMobile ? 2 : 2.5}
      justifyContent="center"
      alignItems={isMobile ? 'stretch' : 'flex-end'}
      sx={{ mb: 4 }}
    >
      {visibleEntries.map((entry) => {
        const height = isMobile ? (entry.rank === 1 ? 180 : 140) : PODIUM_HEIGHT[entry.rank];
        return (
          <Paper
            key={entry.rank}
            elevation={entry.rank === 1 ? 6 : 3}
            sx={{
              flex: isMobile ? undefined : '1 1 0',
              maxWidth: isMobile ? undefined : 280,
              minHeight: height,
              px: 3,
              py: 2.5,
              background: PODIUM_GRADIENTS[entry.rank],
              color: PODIUM_TEXT_COLOR[entry.rank],
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              transform: !isMobile && entry.rank === 1 ? 'translateY(-8px)' : undefined,
              transition: 'transform 200ms ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 90,
                height: 90,
                background: 'rgba(255,255,255,0.12)',
                borderRadius: '50%',
                transform: 'translate(30%, -30%)',
              },
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  opacity: 0.85,
                  color: 'inherit',
                }}
              >
                {PODIUM_LABEL[entry.rank]}
              </Typography>
              <Typography
                sx={{
                  fontSize: entry.rank === 1 ? 88 : 64,
                  fontWeight: 900,
                  lineHeight: 1,
                  mt: 0.5,
                  color: 'inherit',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {entry.rank}
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', mt: 1.5 }}>
              <Typography
                variant={entry.rank === 1 ? 'h5' : 'h6'}
                sx={{
                  fontWeight: 700,
                  color: 'inherit',
                  lineHeight: 1.2,
                  wordBreak: 'normal',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {entry.teamName}
              </Typography>
              {showStartNumber && (
                <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8, fontWeight: 600 }}>
                  startovní číslo {entry.startNumber}
                </Typography>
              )}
              <Typography
                sx={{
                  mt: 1,
                  fontSize: entry.rank === 1 ? 32 : 26,
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  lineHeight: 1,
                  color: 'inherit',
                  letterSpacing: '-0.02em',
                }}
              >
                {entry.primaryValue}
              </Typography>
              {entry.primaryLabel && (
                <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8, fontWeight: 600 }}>
                  {entry.primaryLabel}
                </Typography>
              )}
              {entry.secondaryValue && (
                <Typography variant="caption" sx={{ display: 'block', color: 'inherit', opacity: 0.7 }}>
                  {entry.secondaryValue}
                </Typography>
              )}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};
